/**
 * Finans özeti — A4 dikey tek sayfa.
 * Gün sonu WhatsApp gönderiminde AYNI PDF'in son sayfası olarak eklenir
 * (kullanıcı ayrı ikinci dosya istemiyor); tek başına da üretilebilir.
 * İçerik, Finans ekranının "Özet" sekmesiyle birebir aynı:
 * KPI kartları, gider dağılımı, dönem özeti ve finansal durum.
 */
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { ensureTurkishFonts } from '../pdfTurkishFont';
import { expenseItemBreakdown } from './financePnl';
import { getLiveHasRates } from './financeGoldRate';

const C = {
  ink: [15, 23, 42],
  muted: [100, 116, 139],
  border: [210, 220, 232],
  panel: [248, 250, 252],
  ciro: [37, 99, 235],
  maliyet: [217, 119, 6],
  brut: [13, 148, 136],
  gider: [219, 68, 55],
  netUp: [22, 163, 74],
  netDown: [220, 38, 38],
  marj: [109, 40, 217]
};

const money = (value) =>
  `${Number(value || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`;

const pct = (value) =>
  value == null
    ? '—'
    : `%${Number(value).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Tutarın canlı has satış fiyatına göre gram karşılığı (ekrandaki kartlarla aynı kural). */
function hasGram(amountTry, hasSellTry) {
  if (amountTry == null || amountTry === '') return '';
  const amount = Number(amountTry);
  const rate = Number(hasSellTry);
  if (!Number.isFinite(amount) || !Number.isFinite(rate) || rate <= 0) return '';
  const gram = Math.abs(amount) / rate;
  const digits = gram >= 100 ? 1 : 2;
  return `${gram.toLocaleString('tr-TR', { minimumFractionDigits: digits, maximumFractionDigits: digits })} gr HAS`;
}

const ymdToTr = (ymd) => {
  const parts = String(ymd || '').split('-');
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : String(ymd || '');
};

function drawHeader(doc, { branchLabel, periodLabel, pageW, margin }) {
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageW, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('DejaVuSans', 'bold');
  doc.setFontSize(11);
  doc.text('FİNANS ÖZETİ', margin, 9.6);
  doc.setFont('DejaVuSans', 'normal');
  doc.setFontSize(8.4);
  doc.text(`${branchLabel} · ${periodLabel}`, pageW - margin, 9.6, { align: 'right' });
  doc.setTextColor(...C.ink);
}

/** Ekrandaki KPI kartlarının PDF karşılığı: etiket, tutar ve altında gram karşılığı. */
function drawKpiCards(doc, { cards, x, y, w, h, gap }) {
  const perRow = 3;
  const cardW = (w - gap * (perRow - 1)) / perRow;
  const rows = Math.ceil(cards.length / perRow);
  const cardH = (h - gap * (rows - 1)) / rows;

  cards.forEach((card, i) => {
    const cx = x + (i % perRow) * (cardW + gap);
    const cy = y + Math.floor(i / perRow) * (cardH + gap);
    doc.setDrawColor(...C.border);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(cx, cy, cardW, cardH, 1.4, 1.4, 'FD');
    // sol kenarda renk şeridi (ekrandaki kart vurgusu)
    doc.setFillColor(...card.color);
    doc.roundedRect(cx, cy, 1.3, cardH, 0.6, 0.6, 'F');

    doc.setFont('DejaVuSans', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C.muted);
    doc.text(String(card.label).toUpperCase(), cx + 3.5, cy + 5);

    doc.setFont('DejaVuSans', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(...(card.valueColor || C.ink));
    doc.text(String(card.value), cx + 3.5, cy + 11.4);

    if (card.sub) {
      doc.setFont('DejaVuSans', 'normal');
      doc.setFontSize(6.8);
      doc.setTextColor(...C.muted);
      doc.text(String(card.sub), cx + 3.5, cy + 15.6);
    }
    doc.setTextColor(...C.ink);
  });
  return y + h;
}

function drawPanelTitle(doc, title, x, y, w) {
  doc.setFillColor(...C.panel);
  doc.setDrawColor(...C.border);
  doc.roundedRect(x, y, w, 6.2, 1.2, 1.2, 'FD');
  doc.setFont('DejaVuSans', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...C.ink);
  doc.text(title, x + 2.5, y + 4.2);
  return y + 6.2;
}

function drawRows(doc, rows, { x, y, w }) {
  autoTable(doc, {
    startY: y,
    margin: { left: x, right: 0 },
    tableWidth: w,
    theme: 'plain',
    styles: {
      font: 'DejaVuSans',
      fontSize: 7.6,
      cellPadding: { top: 1.1, bottom: 1.1, left: 2.5, right: 2.5 },
      textColor: C.ink,
      lineColor: C.border,
      lineWidth: { bottom: 0.1 }
    },
    columnStyles: {
      0: { cellWidth: w * 0.58 },
      1: { cellWidth: w * 0.42, halign: 'right', fontStyle: 'bold' }
    },
    body: rows.map((r) => [r[0], r[1]])
  });
  return doc.lastAutoTable.finalY;
}

/**
 * @returns {Promise<{ blob: Blob, base64: string, filename: string, dataUri: string }>}
 */
/**
 * Finans özetini VERİLEN dokümana çizer (gün sonu PDF'inin sonuna eklemek için).
 * Fontlar çağıran tarafta yüklenmiş olmalı; emin olmak için tekrar yükleniyor.
 * `embedded: true` iken sayfa altına kendi dipnotunu yazmaz — gün sonu PDF'inin
 * ortak sayfa altlığı zaten basılıyor.
 */
export async function drawFinanceSummaryPage(doc, {
  branchLabel,
  range,
  pnl,
  giderler = [],
  prices = [],
  dateLabel,
  embedded = false
}) {
  await ensureTurkishFonts(doc);

  const pageW = doc.internal.pageSize.getWidth();
  const margin = 8;
  const w = pageW - margin * 2;
  const hasSell = getLiveHasRates(prices).sell;
  const netLoss = (pnl?.netProfitTry || 0) < 0;
  const missing = pnl?.costMissingSalesCount || 0;
  const periodLabel = `${ymdToTr(range?.startYmd)} – ${ymdToTr(range?.endYmd)}`;

  drawHeader(doc, { branchLabel, periodLabel, pageW, margin });

  let y = 19;
  y = drawKpiCards(doc, {
    x: margin,
    y,
    w,
    h: 40,
    gap: 3,
    cards: [
      { label: 'Ciro', value: money(pnl.revenueTry), sub: hasGram(pnl.revenueTry, hasSell), color: C.ciro },
      { label: 'Maliyet', value: money(pnl.cogsTry), sub: hasGram(pnl.cogsTry, hasSell), color: C.maliyet },
      { label: 'Brüt Kâr', value: money(pnl.grossProfitTry), sub: hasGram(pnl.grossProfitTry, hasSell), color: C.brut },
      { label: 'Gider', value: money(pnl.expenseTry), sub: hasGram(pnl.expenseTry, hasSell), color: C.gider },
      {
        label: netLoss ? 'Net Zarar' : 'Net Kâr',
        value: money(Math.abs(pnl.netProfitTry || 0)),
        sub: hasGram(pnl.netProfitTry, hasSell),
        color: netLoss ? C.netDown : C.netUp,
        valueColor: netLoss ? C.netDown : C.netUp
      },
      { label: 'Net Kâr Marjı', value: pct(pnl.netMarginPct), sub: '', color: C.marj }
    ]
  });

  // --- Dönem Özeti (sol) / Finansal Durum (sağ)
  const colGap = 4;
  const colW = (w - colGap) / 2;
  const rightX = margin + colW + colGap;
  const panelY = y + 4;

  const ozetRows = [
    ['Satış sayısı', String(pnl.salesCount || 0)],
    ...(pnl.purchaseCount ? [['Alış sayısı', String(pnl.purchaseCount)]] : []),
    ['Ciro', money(pnl.revenueTry)],
    ['Maliyet', missing ? `${money(pnl.cogsTry)}*` : money(pnl.cogsTry)],
    ['Satış kârı', missing ? `${money(pnl.saleProfitTry || 0)}*` : money(pnl.saleProfitTry || 0)],
    ...(pnl.purchaseCount ? [['Alış kârı', money(pnl.purchaseProfitTry || 0)]] : []),
    ['Brüt kâr', missing ? `${money(pnl.grossProfitTry)}*` : money(pnl.grossProfitTry)],
    ['Maliyet kapsama', pnl.salesCount === 0 ? '—' : `%${pnl.costCoveragePct}`]
  ];
  let leftY = drawPanelTitle(doc, 'Dönem Özeti', margin, panelY, colW);
  leftY = drawRows(doc, ozetRows, { x: margin, y: leftY + 1, w: colW });

  const durumRows = [
    ['Toplam gider', money(pnl.expenseTry)],
    [netLoss ? 'Net zarar' : 'Net kâr', missing ? `${money(Math.abs(pnl.netProfitTry || 0))}*` : money(Math.abs(pnl.netProfitTry || 0))],
    ['Net kâr marjı', pct(pnl.netMarginPct)],
    ['Dönem', periodLabel],
    ['Rapor tarihi', dateLabel || '']
  ];
  let rightY = drawPanelTitle(doc, 'Finansal Durum', rightX, panelY, colW);
  rightY = drawRows(doc, durumRows, { x: rightX, y: rightY + 1, w: colW });

  if (missing) {
    doc.setFillColor(254, 243, 199);
    doc.setDrawColor(245, 158, 11);
    doc.roundedRect(rightX, rightY + 2, colW, 9, 1.2, 1.2, 'FD');
    doc.setFont('DejaVuSans', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(180, 83, 9);
    doc.text(`${missing} satışta maliyet bilgisi eksik`, rightX + 2.5, rightY + 5.4);
    doc.setFont('DejaVuSans', 'normal');
    doc.setFontSize(6.6);
    doc.text(`Maliyet kapsama %${pnl.costCoveragePct} — kâr rakamları tam olmayabilir.`, rightX + 2.5, rightY + 8.2);
    doc.setTextColor(...C.ink);
    rightY += 11;
  }

  // --- Gider dağılımı
  // POS komisyonu kâr hesabına giriyor ama PDF'te ayrı satır istenmedi
  // (kullanıcı kararı 2026-09-29); tabloda sadece girilen giderler listelenir.
  const giderSatirlari = expenseItemBreakdown(giderler, range);
  let giderY = Math.max(leftY, rightY) + 4;
  giderY = drawPanelTitle(doc, 'Gider Dağılımı', margin, giderY, w);
  if (giderSatirlari.length) {
    const toplam = giderSatirlari.reduce((sum, row) => sum + (row.amountTry || 0), 0);
    autoTable(doc, {
      startY: giderY + 1,
      margin: { left: margin, right: margin },
      tableWidth: w,
      theme: 'grid',
      styles: {
        font: 'DejaVuSans',
        fontSize: 7.4,
        cellPadding: { top: 1, bottom: 1, left: 2, right: 2 },
        textColor: C.ink,
        lineColor: C.border,
        lineWidth: 0.1
      },
      headStyles: { font: 'DejaVuSans', fontStyle: 'bold', fillColor: C.panel, textColor: C.ink, fontSize: 7.2 },
      columnStyles: {
        0: { cellWidth: w * 0.42 },
        1: { cellWidth: w * 0.2 },
        2: { cellWidth: w * 0.21, halign: 'right' },
        3: { cellWidth: w * 0.17, halign: 'right' }
      },
      head: [['Gider', 'Kategori', 'Bu döneme düşen', 'Pay']],
      body: giderSatirlari.map((row) => [
        row.title,
        row.category,
        money(row.amountTry),
        toplam > 0 ? pct((row.amountTry / toplam) * 100) : '—'
      ]),
      foot: [['TOPLAM', '', money(toplam), '%100']],
      footStyles: { font: 'DejaVuSans', fontStyle: 'bold', fillColor: C.panel, textColor: C.ink, fontSize: 7.4 }
    });
    giderY = doc.lastAutoTable.finalY;
  } else {
    doc.setFont('DejaVuSans', 'normal');
    doc.setFontSize(7.6);
    doc.setTextColor(...C.muted);
    doc.text('Bu dönemde gider kaydı yok.', margin + 2.5, giderY + 5);
    doc.setTextColor(...C.ink);
  }

  if (!embedded) {
    const pageH = doc.internal.pageSize.getHeight();
    doc.setFont('DejaVuSans', 'normal');
    doc.setFontSize(6.4);
    doc.setTextColor(...C.muted);
    doc.text('UKBA Kasa · finans özeti', pageW / 2, pageH - 3.5, { align: 'center' });
  }

  return doc;
}

/** Tek başına finans özeti PDF'i (Finans ekranından indirme vb.). */
export async function buildFinanceSummaryPdf(params) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  await drawFinanceSummaryPage(doc, params);

  const blob = doc.output('blob');
  const dataUri = doc.output('datauristring');
  const base64 = String(dataUri).split(',')[1] || '';
  const stamp = String(params?.range?.endYmd || '').replace(/-/g, '');
  return { blob, base64, dataUri, filename: `UKBA_Finans_${stamp || 'ozet'}.pdf` };
}
